import useIntersectionObserver from '@/hooks/shared/useIntersectionObserver';

import { InfoItemType } from './solutionItemTypes';

type SolutionInfoProps = {
  infoItems: InfoItemType[];
};

/**
 * grid-template-columns 가 infoItems.length 로 결정되는 런타임 값이라
 * JIT 가 클래스명을 스캔할 수 있도록 정적 lookup map 으로 둔다.
 * 원본은 `repeat(n, 1fr)` 이다. Tailwind 의 grid-cols-{n} 은
 * `repeat(n, minmax(0, 1fr))` 로 트랙 최소값이 달라지므로 임의값으로 그대로 옮긴다.
 */
const LG_GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-[repeat(1,1fr)]',
  2: 'lg:grid-cols-[repeat(2,1fr)]',
  3: 'lg:grid-cols-[repeat(3,1fr)]',
  4: 'lg:grid-cols-[repeat(4,1fr)]',
  5: 'lg:grid-cols-[repeat(5,1fr)]',
  6: 'lg:grid-cols-[repeat(6,1fr)]',
};

/**
 * 원본과 동일하게 lg 에서도 :not(:last-child) 선택자로 border-bottom 을 다시 지운다.
 * lg:border-b-0 (특이도 0,1,0) 만으로는 base 의
 * [&:not(:last-child)]:border-b (특이도 0,2,0) 를 이기지 못하기 때문이다.
 */
const INFO_ITEM_CLASS =
  'intersection-reveal h-full px-0 py-3 ' +
  '[&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-apps-coreProductDivider ' +
  'lg:px-6 lg:py-1 lg:[&:not(:last-child)]:border-b-0 ' +
  'lg:[&:not(:last-child)]:border-r lg:[&:not(:last-child)]:border-r-apps-coreProductDivider';

export default function SolutionInfo({ infoItems }: SolutionInfoProps) {
  const container = useIntersectionObserver<HTMLDivElement>({
    selector: '[data-reveal-item]',
    staggerMs: 150,
  });

  const columnsClass = LG_GRID_COLUMNS[infoItems.length] ?? LG_GRID_COLUMNS[1];

  /*
   * 원본은 `-mx-6`(-24px) 과 `px-3`(12px) 을 겹쳐 콘텐츠 박스를 24px 넓혔다.
   * 그런데 -24px 은 페이지 gutter(20px)보다 커서 양옆으로 4px 씩 삐져나가고,
   * 그만큼 문서 전체에 가로 스크롤이 생겼다(lg 이상 모든 폭).
   * `-mx-3` 단독도 콘텐츠 박스를 똑같이 24px 넓히므로 항목 위치와 구분선은
   * 그대로 두고 넘침만 사라진다.
   */
  return (
    <div
      ref={container}
      className={`-my-3 lg:-mx-3 lg:my-0 lg:grid lg:items-start ${columnsClass}`}
    >
      {infoItems.map((item, index) => (
        <div key={index} data-reveal-item className={INFO_ITEM_CLASS}>
          <div className="grid grid-cols-[88px_auto] gap-2 lg:grid-cols-[auto] lg:gap-1">
            <div className="text-[14px] font-bold leading-[140%] tracking-[-0.28px] text-apps-brandGray50 lg:text-[16px] lg:tracking-[-0.32px]">
              {item.title}
            </div>
            <div className="text-[14px] font-medium leading-[140%] tracking-[-0.28px] text-apps-brandGray100 lg:text-[16px] lg:tracking-[-0.32px]">
              {item.description}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
